// @oasis-permission-inherits: /moderation (content-moderation:view)
import { useOnArrayChangeEffect, useOnUnmountEffect } from "@mirage-web/utils/react-helpers";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import { getPaginationRange } from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Label } from "~/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";
import { ActionPill } from "~/modules/mirage/components/pill";
import { fetchContentModerationVideosQuery } from "~/modules/moderation/queries";
import type { ContentModerationVideo } from "~/modules/moderation/schemas";

const MIRAGE_ORC_CALLER = "mirage_orc";

const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const targetDate = typeof date === "string" ? new Date(date) : date;
  const diffMs = now.getTime() - targetDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return "just now";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  return targetDate.toLocaleDateString();
};

export const Route = createFileRoute("/_dash/moderation/")({
  validateSearch: z.object({
    page: z.number().optional(),
    lookbackDays: z.enum(["1", "7", "30"]).optional().default("7"),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { page, lookbackDays } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [isGloballyUncensored, setIsGloballyUncensored] = useState(false);
  const [uncensoredVideos, setUncensoredVideos] = useState<Set<string>>(new Set());

  const lookbackDaysNumber = parseInt(lookbackDays);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    fetchContentModerationVideosQuery.useInfiniteQuery({
      params: {
        pageSize: 20,
        lookbackDays: lookbackDaysNumber,
      },
    });

  const totalPages = data?.pages.length ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(
    page && page <= totalPages ? page - 1 : 0
  );

  const currentPageData = data?.pages[currentPageIndex];

  const [videoCache, setVideoCache] = useState(new Map<string, string>());

  const processVideos = useCallback(async () => {
    const videos = data?.pages[currentPageIndex]?.videos.page;
    if (!videos) {
      return;
    }

    for (const [id, url] of videoCache.entries()) {
      if (!videos.find((video) => video.id === id)) {
        URL.revokeObjectURL(url);
        setVideoCache((prev) => {
          const newCache = new Map(prev);
          newCache.delete(id);
          return newCache;
        });
      }
    }

    videos
      .filter((video) => !videoCache.has(video.id))
      .forEach((video) => {
        const blobUrl = video.videoUrl;
        setVideoCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(video.id, blobUrl);
          return newCache;
        });
      });
  }, [data?.pages, currentPageIndex, videoCache]);

  useOnArrayChangeEffect([data, currentPageIndex], () => {
    processVideos();
  });

  useOnUnmountEffect(() => {
    for (const [id, url] of videoCache) {
      URL.revokeObjectURL(url);
      setVideoCache((prev) => {
        const newCache = new Map(prev);
        newCache.delete(id);
        return newCache;
      });
    }
  });

  const paginationRange = useMemo(
    () => getPaginationRange(currentPageIndex, totalPages),
    [currentPageIndex, totalPages]
  );

  const handlePageChange = async (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);

    if (pageIndex === totalPages - 1 && hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const handleVideoClick = (videoId: string) => {
    setUncensoredVideos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Moderation Dashboard</h1>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Lookback days filter */}
          <div className="flex items-center gap-2">
            <Label htmlFor="lookback-days">Period:</Label>
            <Select
              value={lookbackDays}
              onValueChange={(value) => {
                navigate({
                  search: (prev) => ({
                    ...prev,
                    lookbackDays: value as "1" | "7" | "30",
                  }),
                });
              }}
            >
              <SelectTrigger id="lookback-days" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Uncensor toggle */}
        <div className="flex items-center space-x-2">
          <Switch
            id="uncensor-mode"
            checked={isGloballyUncensored}
            onCheckedChange={(checked) => {
              setIsGloballyUncensored(checked);
              if (!checked) {
                setUncensoredVideos(new Set());
              }
            }}
          />
          <Label htmlFor="uncensor-mode" className="cursor-pointer">
            Uncensor all
          </Label>
        </div>
      </div>

      {/* Video Grid */}
      {isLoading && currentPageIndex === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <VideoCardSkeleton key={index} />
          ))}
        </div>
      )}
      {!(isLoading && currentPageIndex === 0) && currentPageData?.videos.page.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">No videos found for the selected period</p>
        </div>
      )}
      {!(isLoading && currentPageIndex === 0) && currentPageData?.videos.page.length !== 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {currentPageData?.videos.page.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              videoUrl={videoCache.get(video.id) ?? video.videoUrl}
              isBlurred={!isGloballyUncensored && !uncensoredVideos.has(video.id)}
              onVideoClick={() => handleVideoClick(video.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => handlePageChange(Math.max(0, currentPageIndex - 1))}
                className={cn(currentPageIndex === 0 && "pointer-events-none opacity-50")}
              />
            </PaginationItem>

            {paginationRange[0] > 0 && (
              <>
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(0)}>1</PaginationLink>
                </PaginationItem>
                {paginationRange[0] > 1 && <span className="px-2">...</span>}
              </>
            )}

            {paginationRange.map((pageIndex) => (
              <PaginationItem key={pageIndex}>
                <PaginationLink
                  onClick={() => handlePageChange(pageIndex)}
                  isActive={pageIndex === currentPageIndex}
                >
                  {pageIndex + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            {paginationRange[paginationRange.length - 1] < totalPages - 1 && (
              <>
                {paginationRange[paginationRange.length - 1] < totalPages - 2 && (
                  <span className="px-2">...</span>
                )}
                <PaginationItem>
                  <PaginationLink onClick={() => handlePageChange(totalPages - 1)}>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
            )}

            <PaginationItem>
              <PaginationNext
                onClick={() => handlePageChange(Math.min(totalPages - 1, currentPageIndex + 1))}
                className={cn(
                  (currentPageIndex === totalPages - 1 || totalPages === 0) &&
                    "pointer-events-none opacity-50"
                )}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

interface VideoCardProps {
  video: ContentModerationVideo;
  videoUrl: string;
  isBlurred: boolean;
  onVideoClick: () => void;
}

function VideoCard({ video, videoUrl, isBlurred, onVideoClick }: VideoCardProps) {
  // Helper function to get badge variant based on score
  const getBadgeVariant = (score: number) => {
    if (score >= 3) {
      return "destructive";
    }
    if (score >= 2) {
      return "secondary";
    }
    return "outline";
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <div className="relative aspect-[9/16] bg-muted">
        <video
          src={videoUrl}
          className={cn(
            "w-full h-full object-cover transition-all duration-300",
            isBlurred && "blur-xl"
          )}
          controls
          preload="metadata"
        />

        {isBlurred && (
          <button
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 hover:bg-black/40 transition-colors cursor-pointer"
            onClick={onVideoClick}
            aria-label="Click to reveal content"
          >
            <span className="text-white font-medium">Content Hidden</span>
            <span className="text-white text-sm mt-1">Click to reveal</span>
          </button>
        )}

        {/* Moderated classes badges - always visible on top */}
        {video.moderatedClasses && video.moderatedClasses.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-col gap-1 z-10 pointer-events-none">
            {video.moderatedClasses.map((modClass, index) => (
              <Badge
                key={`${modClass.class}-${index}`}
                variant={getBadgeVariant(modClass.score) as "destructive" | "secondary" | "outline"}
                className="text-xs"
              >
                {modClass.class} ({modClass.score})
              </Badge>
            ))}
          </div>
        )}

        {/* Toggle blur button when video is not blurred */}
        {!isBlurred && (
          <button
            className="absolute top-2 right-2 bg-black/70 hover:bg-black/80 text-white px-2 py-1 rounded text-xs transition-colors"
            onClick={onVideoClick}
            aria-label="Hide content"
          >
            Hide
          </button>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{formatRelativeTime(video.createdAt)}</span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Caller:</span>
            {video.caller === MIRAGE_ORC_CALLER && video.callerOperationId ? (
              <Link
                to="/videos/view/$videoId"
                params={{ videoId: video.callerOperationId }}
                className="text-primary hover:underline"
              >
                {video.caller}
              </Link>
            ) : (
              <span className="text-muted-foreground">{video.caller}</span>
            )}
          </div>
          <div className="flex items-start gap-2">
            <ActionPill
              label="User ID"
              actions={[
                {
                  type: "copy",
                  label: video.userId,
                  value: video.userId,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoCardSkeleton() {
  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <Skeleton className="aspect-[9/16] w-full" />
      <div className="p-3 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  );
}
